import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import { AuthContext } from "src/contexts/AuthContext";
import { Property } from "src/interfaces/IProperty";
import { AlenRoom } from "src/interfaces/IZone";
import { AlenDevice } from "src/interfaces/IAlenDevice";
import { AlenAccessToken } from "../authService";
import { makeAlenAxios } from ".";

const UNKNOWN_LABEL = "Unknown";

const DATA_SYNC_INTERVAL = 60000; // 1 minute
type FetchAlenDevicesResponse = {
  result: AlenDevice[];
};
const fetchAlenPropertyDevices = async (
  alenAccess: AlenAccessToken,
  propertyId: number,
  alenPropertyName?: string
) => {
  const alenAxios = makeAlenAxios(alenAccess);
  const response = await alenAxios.get<FetchAlenDevicesResponse>(
    `/homes/${propertyId}/devices`
  );
  response.data = {
    ...response.data,
    result: response.data.result.map((device) => ({
      ...device,
      location_name: alenPropertyName ?? UNKNOWN_LABEL,
    })),
  };
  return response.data.result;
};

const fetchAlenRoomDevices = async (
  alenAccess: AlenAccessToken,
  propertyId: number,
  roomId: number,
  roomName?: string
) => {
  const alenAxios = makeAlenAxios(alenAccess);
  const response = await alenAxios.get<FetchAlenDevicesResponse>(
    `/homes/${propertyId}/rooms/${roomId}/devices`
  );
  response.data = {
    ...response.data,
    result: response.data.result.map((device) => ({
      ...device,
      location_name: roomName ?? "Unknown",
    })),
  };
  return response.data.result;
};

/**
 * Fetches devices for a given property or rooms.
 *
 * Note: This function can break if the number of rooms exceeds the API request rate limit.
 *
 * @param property - The property for which to fetch devices.
 * @param rooms - The rooms within the property.
 * @returns A list of devices.
 */
const useAlenDevices = (
  property: Property | undefined,
  rooms: AlenRoom[] | null
) => {
  const { user } = useContext(AuthContext);
  const [data, setData] = useState<AlenDevice[] | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AxiosError | undefined>();

  const fetchAlenDevices = useCallback(async () => {
    const alenPropertyId = property?.alen_id;
    if (!alenPropertyId) {
      setLoading(false);
      setData([]);
      return;
    };
    if (!user || !user.alen) return;
    const token = user.alen;
    try {
      let devices: AlenDevice[][];
      if (rooms === null) {
        devices = [
          await fetchAlenPropertyDevices(
            token,
            alenPropertyId,
            property.property_name
          ),
        ];
      } else {
        devices = await Promise.all(
          rooms.map((room) =>
            fetchAlenRoomDevices(token, alenPropertyId, room.room_id, room.name)
          )
        );
      }
      setData(devices.flat());
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        setError(err);
      } else {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    }
  }, [property, rooms, user]);
  useEffect(() => {
    setLoading(true);
    fetchAlenDevices().finally(() => setLoading(false));
  }, [fetchAlenDevices]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchAlenDevices();
    }, DATA_SYNC_INTERVAL);

    return () => clearInterval(intervalId); // Cleanup interval on unmount
  }, [fetchAlenDevices]);

  const deviceData = useMemo(() => data, [data]);
  return { data: deviceData, loading, error, refetch: fetchAlenDevices };
};

export default useAlenDevices;
